import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Asterisk } from "@/components/Reveal";
import { LangToggle } from "@/components/Navbar";
import { useLanguage } from "@/LanguageContext";

const content = {
  it: {
    back: "Torna al sito",
    updated: "Ultimo aggiornamento: 1 Settembre 2026",
    intro:
      "La presente informativa descrive le modalità di gestione del sito web di studio noom in riferimento al trattamento dei dati personali degli utenti che lo consultano e usufruiscono dei relativi servizi.",
    outro: "Per esercitare qualsiasi diritto, è sufficiente inviare un'email all'indirizzo:",
    sections: [
      {
        title: "1. Titolare del Trattamento",
        body: [
          "Il Titolare del trattamento dei dati personali è: studio noom di Giorgia Marie Claire Sgarbi — Via Luigi Galvani 4, 51057 San Vito di Spilamberto, MO, ITALY. C.F. SGRGGM98B53B819M, P. IVA 04242440362. Email di contatto: giorgia@studionoom.it.",
        ],
      },
      {
        title: "2. Tipologie di Dati Raccolti",
        body: ["Il sito raccoglie dati personali in modo autonomo o tramite terze parti:"],
        list: [
          "Dati forniti volontariamente dall'utente: nome, cognome, indirizzo email, numero di telefono, dettagli sull'attività o risposte inserite nel modulo di contatto e nei questionari di candidatura/brief.",
          "Dati di navigazione e tecnici: indirizzi IP, tipo di browser, parametri del dispositivo e orari di accesso, raccolti automaticamente per consentire il corretto funzionamento e la sicurezza del sito web.",
          "Dati per la prenotazione di appuntamenti: dati di contatto inseriti tramite piattaforme esterne di prenotazione (Calendly) per fissare le call conoscitive.",
        ],
      },
      {
        title: "3. Finalità e Base Giuridica del Trattamento",
        body: ["I dati dell'utente vengono trattati per le seguenti finalità:"],
        list: [
          "Rispondere a richieste e preventivi: gestire le comunicazioni inviate tramite form o email e organizzare le call conoscitive (Base giuridica: esecuzione di misure precontrattuali).",
          "Esecuzione dei servizi professionali: svolgere le attività di consulenza, brand strategy, visual design e restyling concordate (Base giuridica: esecuzione di un contratto).",
          "Obblighi di legge: adempiere a obblighi fiscali, contabili e normativi (Base giuridica: obbligo legale).",
          "Sicurezza e manutenzione: garantire la corretta visualizzazione e prevenire frodi o usi illeciti del sito web (Base giuridica: legittimo interesse del titolare).",
        ],
      },
      {
        title: "4. Modalità di Trattamento e Conservazione",
        body: [
          "Il trattamento viene effettuato mediante strumenti informatici e telematici con misure di sicurezza adeguate a prevenire accessi non autorizzati, divulgazione, modifica o distruzione dei dati.",
          "I dati legati a richieste di informazioni o call conoscitive vengono conservati per il tempo strettamente necessario a gestire la richiesta.",
          "I dati amministrativi e di fatturazione vengono conservati per i termini previsti dalla legge (10 anni).",
        ],
      },
      {
        title: "5. Comunicazione dei Dati a Terzi",
        body: ["I dati raccolti non saranno mai ceduti a terzi per scopi di marketing. Potranno essere condivisi esclusivamente con:"],
        list: [
          "Fornitori di servizi tecnici e hosting che supportano il funzionamento del sito web.",
          "Piattaforme terze integrate (ad esempio software di gestione appuntamenti come Calendly o software di gestione email) che agiscono come responsabili del trattamento.",
          "Consulenti contabili/fiscali e autorità competenti nei soli casi previsti dalla legge.",
        ],
      },
      {
        title: "6. Cookie e Tracciamento",
        body: [
          "Questo sito utilizza cookie tecnici strettamente necessari al funzionamento della navigazione e, ove attivi, strumenti di analisi anonimizzati per monitorare il traffico. Per la gestione completa delle preferenze relative ai cookie, fare riferimento all'apposito banner/sezione Cookie del sito.",
        ],
      },
      {
        title: "7. Diritti dell'Interessato (GDPR)",
        body: ["In ogni momento, l'utente può esercitare i propri diritti ai sensi degli artt. 15-22 del Regolamento UE 2016/679, tra cui:"],
        list: [
          "Chiedere l'accesso ai propri dati personali, la rettifica o la cancellazione degli stessi.",
          "Chiedere la limitazione del trattamento o opporsi al trattamento.",
          "Richiedere la portabilità dei dati.",
          "Proporre reclamo all'autorità di controllo competente (in Italia: Garante per la Protezione dei Dati Personali).",
        ],
        outro: true,
      },
    ],
  },
  en: {
    back: "Back to site",
    updated: "Last updated: September 1, 2026",
    intro:
      "This privacy policy describes how studio noom manages this website regarding the processing of personal data of users who consult it and use its services.",
    outro: "To exercise any of these rights, simply send an email to:",
    sections: [
      {
        title: "1. Data Controller",
        body: [
          "The Data Controller for personal data is: studio noom di Giorgia Marie Claire Sgarbi — Spilamberto, 41057 Modena, Italy. VAT Number (P.IVA): 04242440362. Contact Email: giorgia@studionoom.it.",
        ],
      },
      {
        title: "2. Types of Data Collected",
        body: ["This website collects personal data independently or through third parties:"],
        list: [
          "Data provided voluntarily by the user: first name, last name, email address, phone number, business details, or answers submitted via contact forms, application questionnaires, and project briefs.",
          "Technical and browsing data: IP addresses, browser types, device parameters, and access times, automatically collected to ensure the proper operation and security of the website.",
          "Appointment booking data: contact details entered through external booking platforms (e.g., Calendly) to schedule discovery calls.",
        ],
      },
      {
        title: "3. Purpose and Legal Basis for Processing",
        body: ["User data is processed for the following purposes:"],
        list: [
          "Responding to inquiries and quote requests: managing communications sent via forms or email and scheduling discovery calls (Legal basis: execution of pre-contractual measures).",
          "Performance of professional services: delivering agreed consulting, brand strategy, visual design, and restyling services (Legal basis: performance of a contract).",
          "Legal obligations: complying with tax, accounting, and statutory requirements (Legal basis: legal obligation).",
          "Security and maintenance: ensuring correct display and preventing fraud or misuse of the website (Legal basis: legitimate interest of the Data Controller).",
        ],
      },
      {
        title: "4. Processing Methods and Data Retention",
        body: [
          "Data processing is carried out using IT and digital tools, adopting adequate security measures to prevent unauthorized access, disclosure, modification, or destruction of data.",
          "Data related to information requests or discovery calls is retained for the time strictly necessary to handle the request.",
          "Administrative, accounting, and invoicing data is retained for the statutory terms required by applicable law (10 years).",
        ],
      },
      {
        title: "5. Data Disclosure to Third Parties",
        body: ["Collected data will never be sold or transferred to third parties for marketing purposes. It may be shared exclusively with:"],
        list: [
          "Technical service and hosting providers supporting website operations.",
          "Integrated third-party platforms (e.g., appointment scheduling tools like Calendly or email service providers) acting as data processors.",
          "Accounting/tax advisors and competent legal authorities solely in cases required by law.",
        ],
      },
      {
        title: "6. Cookies and Tracking",
        body: [
          "This website uses technical cookies strictly necessary for browsing and, where active, anonymized analytics tools to monitor traffic. For full management of cookie preferences, please refer to the dedicated Cookie banner/section on the website.",
        ],
      },
      {
        title: "7. Rights of the Data Subject (GDPR)",
        body: ["Under Articles 15–22 of EU Regulation 2016/679 (GDPR), users may exercise their rights at any time, including:"],
        list: [
          "Requesting access to, rectification, or erasure of their personal data.",
          "Requesting restriction of processing or objecting to processing.",
          "Requesting data portability.",
          "Lodging a complaint with the competent supervisory authority (in Italy: Garante per la Protezione dei Dati Personali).",
        ],
        outro: true,
      },
    ],
  },
};

const Privacy = () => {
  const { lang } = useLanguage();
  const c = content[lang] || content.it;

  useEffect(() => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <header className="border-b border-noom-line">
        <div className="mx-auto flex h-16 max-w-[1100px] items-center justify-between px-6 lg:h-20">
          <Link to="/" aria-label="studio noom — home" data-testid="privacy-logo-link">
            <img src="/assets/logo-dark-wide.png" alt="studio noom" className="h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-5">
            <LangToggle />
            <Link
              to="/"
              data-testid="privacy-back-link"
              className="group hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-noom-muted transition-colors duration-300 hover:text-noom-ink sm:inline-flex"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
              {c.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-16 lg:py-24">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-noom-lilacDeep">
          <Asterisk className="h-3 w-3 text-noom-lilac" />
          {c.updated}
        </p>
        <h1 className="mt-6 font-display text-4xl font-semibold tracking-[-0.02em] text-noom-ink sm:text-5xl">
          Privacy Policy
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-noom-muted">{c.intro}</p>

        <div className="mt-14 space-y-12">
          {c.sections.map((s, i) => (
            <section key={i} data-testid={`privacy-section-${i + 1}`} className="border-t border-noom-line pt-8">
              <h2 className="font-display text-xl font-semibold tracking-tight text-noom-ink sm:text-2xl">{s.title}</h2>
              {s.body?.map((p, j) => (
                <p key={j} className="mt-4 max-w-3xl text-sm leading-relaxed text-noom-muted sm:text-base">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-3">
                  {s.list.map((li, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm leading-relaxed text-noom-muted sm:text-base">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-noom-lilac" />
                      {li}
                    </li>
                  ))}
                </ul>
              )}
              {s.outro && (
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-noom-muted sm:text-base">
                  {c.outro}{" "}
                  <a
                    href="mailto:giorgia@studionoom.it"
                    className="font-semibold text-noom-ink underline decoration-noom-lilac decoration-2 underline-offset-4 transition-colors hover:text-noom-lilacDeep"
                  >
                    giorgia@studionoom.it
                  </a>
                  .
                </p>
              )}
            </section>
          ))}
        </div>
      </main>

      <footer className="mt-10 bg-noom-ink py-8">
        <div className="mx-auto flex max-w-[1100px] flex-col items-start justify-between gap-2 px-6 sm:flex-row sm:items-center">
          <p className="font-mono text-[11px] tracking-[0.08em] text-[#8E8E93]">
            © 2026 Studio Noom. Tutti i diritti riservati. — P.IVA 04242440362
          </p>
          <Link
            to="/"
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-noom-lilac transition-colors duration-300 hover:text-white"
          >
            studionoom.it
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
